import { useEffect, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import SearchBar from "../components/SearchBar";
import Colors from '../styles/Colors.json';
import './EmployeeView.css';
import EmployeeViewManager from "../managers/EmployeeViewManager";
import { EmployeeInfo } from "../interfaces/EmployeeInfoInterface";
import { useSiteContext } from "../SiteContext";
import ToggleSwitch from "../components/ToggleSwitch";
import AuditReportModal from "../components/AuditReportModal";

const AuditReportPage: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedSuper, setSelectedSuper] = useState<string>("ALL");
    const [selectedLocation, setSelectedLocation] = useState<string>("ALL");
    const [data, setData] = useState<EmployeeInfo[] | null>(null);
    const [sortedData, setSortedData] = useState<EmployeeInfo[] | null>(null);
    const [isActive, setIsActive] = useState<boolean>(true);
    const [supervisors, setSupervisors] = useState<EmployeeInfo[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(30);
    const [selectedEmployee, setSelectedEmployee] = useState<EmployeeInfo | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const { setLoading, locations } = useSiteContext();

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await EmployeeViewManager.getEmployees();
            setData(response);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch employee data on component mount
    useEffect(() => {
        fetchData();
    }, []);

    // Build supervisor list whenever data or location changes
    useEffect(() => {
        if (!data) return;

        const uniqueSupervisorIDs: number[] = [];
        data.forEach((employee) => {
            if (!uniqueSupervisorIDs.includes(employee.supervisorID)) {
                uniqueSupervisorIDs.push(employee.supervisorID);
            }
        });

        const matchingSupervisors: EmployeeInfo[] = data.filter((employee) => {
            if (!uniqueSupervisorIDs.includes(employee.employeeID)) {
                return false;
            }

            if (selectedLocation !== "ALL") {
                const locationIDNum = parseInt(selectedLocation);
                return employee.locationID === locationIDNum;
            }

            return true;
        });

        setSupervisors(matchingSupervisors);
    }, [data, selectedLocation]);

    // Reset supervisor to "ALL" when location changes
    useEffect(() => {
        setSelectedSuper("ALL");
    }, [selectedLocation]);

    // Sort and filter data when supervisor, location, or active status changes
    useEffect(() => {
        sortData(selectedSuper, selectedLocation, isActive);
    }, [selectedSuper, selectedLocation, isActive, data, supervisors]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Function to sort and filter employee data
    const sortData = async (superID: string, locationID: string, active: boolean) => {
        if (!data) return;
        setLoading(true);

        let newData = data;

        // Filter data based on supervisor, location, and active status
        newData = newData.filter((employee) => {
            const employeeSupervisorID = employee.supervisorID;

            // Match supervisor logic
            let matchesSupervisor = true;
            if (superID !== "ALL") {
                if (superID === "0") {
                    // Match employees with supervisorID === 0
                    matchesSupervisor = employeeSupervisorID === 0;
                } else {
                    // Find the supervisor object that matches the selected superID
                    const selectedSupervisor = supervisors.find(sup => {
                        const supKey = sup.altEmployeeID ?? sup.employeeID.toString();
                        return supKey === superID;
                    });
                    if (selectedSupervisor) {
                        matchesSupervisor = employeeSupervisorID === selectedSupervisor.employeeID;
                    } else {
                        matchesSupervisor = false;
                    }
                }
            }

            const matchesLocation = 
                locationID === "ALL" || 
                employee.locationID === parseInt(locationID);
            const matchesActiveStatus = employee.active === active;
            return matchesSupervisor && matchesLocation && matchesActiveStatus;
        });

        // Sort by altEmployeeID or employeeID
        newData = newData.sort((a, b) => {
            const aID = a.altEmployeeID ?? a.employeeID.toLocaleString();
            const bID = b.altEmployeeID ?? b.employeeID.toLocaleString();
            return aID.localeCompare(bID);
        });

        setSortedData(newData);
        setLoading(false);
    };

    // Filter employees based on search term
    const filteredEmployees = sortedData?.filter((employee) => {
        if (searchTerm.trim() === "") {
            return true;
        }
        const searchTermLower = searchTerm.toLowerCase();

        // Search by name (if search term contains letters)
        if (/[a-zA-Z]/.test(searchTerm)) {
            const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
            return fullName.includes(searchTermLower);
        }

        // Search by employee ID (if search term is numeric)
        if (/^\d+$/.test(searchTerm)) {
            const empID = employee.altEmployeeID ? 
                employee.altEmployeeID.toString().includes(searchTerm) :
                employee.employeeID.toString().includes(searchTerm);
            return empID;
        }

        return false;
    }) || [];

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);

    // Calculate total pages for pagination
    const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
    const maxVisibleButtons = 10;

    // Function to change page
    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    // Function to render pagination buttons
    const renderPaginationButtons = () => {
        if (totalPages === 0) return null;
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
        const buttons = [];
        let startPage, endPage;

        if (totalPages <= maxVisibleButtons) {
            startPage = 1;
            endPage = totalPages;
        } else {
            const halfVisible = Math.floor(maxVisibleButtons / 2);
            if (currentPage <= halfVisible) {
                startPage = 1;
                endPage = maxVisibleButtons;
            } else if (currentPage + halfVisible >= totalPages) {
                startPage = totalPages - maxVisibleButtons + 1;
                endPage = totalPages;
            } else {
                startPage = currentPage - halfVisible;
                endPage = currentPage + halfVisible;
            }
        }

        buttons.push(
            <button
                key="prev"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1 || totalPages <= 1}
            >
                Previous
            </button>
        );

        if (startPage > 1) {
            buttons.push(
                <button key="start-ellipsis" disabled>
                    ...
                </button>
            );
        }

        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    onClick={() => paginate(i)}
                    className={currentPage === i ? "active" : ""}
                >
                    {i}
                </button>
            );
        }

        if (endPage < totalPages) {
            buttons.push(
                <button key="end-ellipsis" disabled>
                    ...
                </button>
            );
        }

        buttons.push(
            <button
                key="next"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                Next
            </button>
        );

        return buttons;
    };

    const handleAuditReportClick = (employee: EmployeeInfo) => {
        setSelectedEmployee(employee);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedEmployee(null);
    };

    return (
        <PageWrapper title="Audit Report" backgroundColor={Colors.White}>
            <div className="employee-view-location-row">
                <div className="employee-view-sort-by">
                    <label>Location: </label>
                    <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)}>
                        <option key={0} value={"ALL"}>
                            ALL
                        </option>
                        {locations?.filter(location => location.testLocation === false).sort((a, b) => {
                            const aKey = a.altLocationID || a.name;
                            const bKey = b.altLocationID || b.name;
                            return aKey.localeCompare(bKey);
                        }).map((location) => (
                            <option key={location.locationID} value={location.locationID.toString()}>
                                {location.altLocationID ? `${location.altLocationID} - ${location.name}` : location.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="employee-view-top-row">
                <div className="employee-view-sort-by">
                    <label>Supervisor: </label>
                    <select value={selectedSuper} onChange={(e) => setSelectedSuper(e.target.value)}>
                        <option key={0} value={"ALL"}>
                            ALL
                        </option>
                        <option key={1} value={"0"}>
                            0 - Supervisors
                        </option>
                        {supervisors?.map((supervisor, index) => {
                            const value = supervisor.altEmployeeID ?? supervisor.employeeID.toString();
                            return (
                                <option key={index} value={value}>
                                    {`${value} - ${supervisor.firstName} ${supervisor.lastName}`}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <ToggleSwitch
                    label="Active:"
                    checked={isActive}
                    onChange={() => setIsActive((prev) => !prev)}
                />
                <SearchBar
                    placeholder={"Search Employees"}
                    value={searchTerm}
                    onChange={setSearchTerm}
                    style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'visible'
                    }}
                />
            </div>
            <div className="employee-view-table">
                <table>
                    <thead>
                        <tr className="employee-view-table-row-blue">
                            <th style={{ cursor: 'default' }}>Employee Name</th>
                            <th style={{ cursor: 'default' }}>Employee ID</th>
                            <th style={{ cursor: 'default' }}>Audit Report</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems?.map((employee, index) => (
                            <tr key={employee.employeeID} className={index % 2 === 1 ? "employee-view-table-row-blue" : "employee-view-table-row-white"}>
                                <td>{employee.firstName + " " + (employee.middleName ? employee.middleName + " " : "") + employee.lastName}</td>
                                <td>{employee.altEmployeeID ?? ''}</td>
                                <td>
                                    <button 
                                        className="edit-role-button"
                                        onClick={() => handleAuditReportClick(employee)}
                                    >
                                        Audit Report
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="pagination">
                {renderPaginationButtons()}
            </div>

            {selectedEmployee && (
                <AuditReportModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    employeeID={selectedEmployee.employeeID}
                    altEmployeeID={selectedEmployee.altEmployeeID || selectedEmployee.employeeID.toString()}
                    employeeName={`${selectedEmployee.firstName} ${selectedEmployee.middleName ? selectedEmployee.middleName + " " : ""}${selectedEmployee.lastName}`}
                />
            )}
        </PageWrapper>
    );
};

export default AuditReportPage;
